import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PublicSettings {
  // Legacy nested shape — kept for backwards compatibility with
  // existing consumers (AICoach.tsx uses settings.limits.*).
  // The AI message quota is now weekly; the monthly fields are retained
  // for backwards compatibility but new consumers should read the
  // _weekly variants.
  limits: {
    free_ai_monthly: number;
    premium_ai_monthly: number;
    free_ai_weekly: number;
    premium_ai_weekly: number;
    free_max_programs: number;
  };
  ui: {
    push_default_enabled: boolean;
  };
  features: {
    marketplace_enabled: boolean;
    ai_coach_enabled: boolean;
    nutrition_tracking_enabled: boolean;
  };

  // Flat fields surfaced from app_settings.global. These mirror the
  // columns added by the app_settings_expand migration and let
  // consumers wire any admin-editable knob without a privileged query.
  workout_reward_tokens: number;
  streak_reward_tokens: number;
  streak_reward_every_days: number;
  referral_reward_tokens: number;
  free_max_templates: number;
  free_ai_messages_per_month: number;
  premium_ai_messages_per_month: number;
  free_ai_messages_per_week: number;
  premium_ai_messages_per_week: number;
  free_custom_exercise_limit: number;
  lifetime_seat_cap: number;
  /** Server-asserted availability for the one-time lifetime purchase.
   *  `get-public-settings` returns true only when its own
   *  STRIPE_PRICE_ID_LIFETIME secret is present (the same secret
   *  `create-lifetime-checkout` requires). Missing, false or unknown
   *  means the lifetime CTA must stay hidden — never infer it from
   *  prices or copy. */
  lifetime_purchase_available: boolean;
  /** Live count of active lifetime entitlements. `null` when the
   *  edge function couldn't compute it — UI should hide the
   *  "X of N left" chip in that case. */
  lifetime_seats_taken: number | null;
  maintenance_mode: boolean;
  maintenance_message: string | null;
  signups_enabled: boolean;
  /** Admin-editable copy for the /upgrade page. Stored as a single
   *  JSONB blob so admins can tweak features and FAQ without a
   *  redeploy. `null` when the column hasn't been seeded yet — the
   *  Upgrade page falls back to its hardcoded defaults in that case. */
  upgrade_page_config: UpgradePageConfig | null;
}

export interface UpgradePageTierFree {
  name: string;
  price_display: string;
  price_unit: string;
  caption: string;
  cta: string;
  features: string[];
}

export interface UpgradePageTierPremium {
  name: string;
  price_monthly_display: string;
  price_annual_display: string;
  monthly_caption: string;
  annual_caption: string;
  cta_monthly: string;
  cta_annual: string;
  popular_badge: string;
  features: string[];
}

export interface UpgradePageTierLifetime {
  name: string;
  price_display: string;
  price_unit: string;
  caption: string;
  cta: string;
  features: string[];
}

export interface UpgradePageFAQItem {
  q: string;
  a: string;
}

export interface UpgradePageConfig {
  title: string;
  subtitle: string;
  tiers: {
    free: UpgradePageTierFree;
    premium: UpgradePageTierPremium;
    lifetime: UpgradePageTierLifetime;
  };
  faq: UpgradePageFAQItem[];
}

// Defaults match the DB column defaults in
// 20260524290000_app_settings_expand.sql. If the row is missing or
// the edge function fails, we still want a workable app.
const defaultSettings: PublicSettings = {
  limits: {
    free_ai_monthly: 30,
    premium_ai_monthly: 500,
    free_ai_weekly: 10,
    premium_ai_weekly: 250,
    free_max_programs: 3,
  },
  ui: {
    push_default_enabled: true,
  },
  features: {
    marketplace_enabled: true,
    ai_coach_enabled: true,
    nutrition_tracking_enabled: true,
  },
  workout_reward_tokens: 2,
  streak_reward_tokens: 5,
  streak_reward_every_days: 7,
  referral_reward_tokens: 50,
  free_max_templates: 50,
  free_ai_messages_per_month: 30,
  premium_ai_messages_per_month: 500,
  free_ai_messages_per_week: 10,
  premium_ai_messages_per_week: 250,
  free_custom_exercise_limit: 10,
  lifetime_seat_cap: 50,
  // Fail closed: if the settings request fails we cannot prove the backend
  // can sell lifetime, so the default is unavailable.
  lifetime_purchase_available: false,
  lifetime_seats_taken: null,
  maintenance_mode: false,
  maintenance_message: null,
  signups_enabled: true,
  upgrade_page_config: null,
};

const PUBLIC_SETTINGS_TIMEOUT_MS = 2500;

async function fetchPublicSettings() {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      supabase.functions.invoke("get-public-settings"),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("Public settings request timed out")),
          PUBLIC_SETTINGS_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Hook to fetch public (non-sensitive) app settings.
 *
 * This replaces direct queries to app_settings table,
 * which is now restricted to admin/superuser only.
 *
 * The edge function returns BOTH the legacy nested shape
 * (limits/ui/features) and the flat columns from app_settings.global so
 * we can keep existing consumers working while wiring new ones to the
 * admin Settings page.
 */
export function usePublicSettings() {
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async (): Promise<PublicSettings> => {
      try {
        const { data, error } = await fetchPublicSettings();

        if (error) {
          return defaultSettings;
        }

        // Merge whatever the function returns over the defaults so any
        // missing column falls back to a sensible value rather than
        // undefined.
        const merged: PublicSettings = {
          ...defaultSettings,
          ...(data ?? {}),
          limits: {
            ...defaultSettings.limits,
            ...((data as any)?.limits ?? {}),
          },
          ui: {
            ...defaultSettings.ui,
            ...((data as any)?.ui ?? {}),
          },
          features: {
            ...defaultSettings.features,
            ...((data as any)?.features ?? {}),
          },
        };

        return merged;
      } catch {
        return defaultSettings;
      }
    },
    // Long stale time — these values change rarely (admin edits), and a
    // stale cap by a few minutes is harmless. Reduces edge function
    // round-trips on every page mount.
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 0,
  });

  return {
    settings: settings ?? defaultSettings,
    isLoading,
    error,
  };
}
